import { useMemo } from "react";
import {
  Link,
  Navigate,
  createSearchParams,
  useSearchParams,
} from "react-router";
import PageShell from "../../../design-system/components/PageShell/PageShell";
import BlockContent from "../../../design-system/components/BlockContent/BlockContent";
import Text from "../../../design-system/primitives/components/Text/Text";
import { MODELS, useModel } from "../../../api";
import type { GlossaryEntry } from "../../../api/spark";
import type { SparkBag } from "../sparkBag";
import { styles } from "./glossaryOverview.css";

type LetterGroup = { letter: string; entries: GlossaryEntry[] };

// Group alphabetically-sorted entries by their (uppercased) first letter.
const groupByLetter = (entries: GlossaryEntry[]): LetterGroup[] => {
  const groups: LetterGroup[] = [];
  for (const entry of entries) {
    const letter = entry.term.charAt(0).toUpperCase();
    const last = groups[groups.length - 1];
    if (last && last.letter === letter) {
      last.entries.push(entry);
    } else {
      groups.push({ letter, entries: [entry] });
    }
  }
  return groups;
};

/**
 * Glossary page for a spark. Opened from the "Glossar" toolbar entry when the
 * spark has `showGlossary` enabled. A 360px term list (grouped by first letter)
 * on the left, the selected term's full entry on the right. The selected term
 * lives in the `?term=` search param; with none selected we redirect to the
 * first term alphabetically.
 */
const GlossaryOverview = ({ bag: { spark } }: { bag: SparkBag }) => {
  // slug is always present once SparkShell has resolved the spark (it redirects
  // on slug mismatch before mounting these routes).
  const entries = useModel(MODELS.SPARK_GLOSSARY(spark.slug!));
  const [searchParams] = useSearchParams();
  const selectedTerm = searchParams.get("term");

  const sorted = useMemo(
    () => [...entries].sort((a, b) => a.term.localeCompare(b.term, "de")),
    [entries],
  );
  const groups = useMemo(() => groupByLetter(sorted), [sorted]);

  const selectedEntry = entries.find((entry) => entry.term === selectedTerm);

  const titleSlot = <Text variant="h4">Glossar</Text>;
  const titleButton = {
    icon: "arrow-left",
    ariaLabel: "Zurück",
    to: "..",
  } as const;

  // Empty glossary: nothing to select or redirect to.
  if (!sorted.length) {
    return (
      <PageShell titleSlot={titleSlot} titleButton={titleButton}>
        <Text variant="body">Für dieses Spark gibt es noch kein Glossar.</Text>
      </PageShell>
    );
  }

  // No (or unknown) term selected: send the user to the first one alphabetically.
  if (!selectedEntry) {
    return (
      <Navigate
        to={{ search: `?${createSearchParams({ term: sorted[0].term })}` }}
        replace
      />
    );
  }

  return (
    <PageShell
      titleSlot={titleSlot}
      titleButton={titleButton}
      scrollBody={false}
    >
      <div className={styles.body}>
        <nav className={styles.list}>
          {groups.map((group) => (
            <div key={group.letter} className={styles.letterGroup}>
              <Text variant="subheader-small">{group.letter}</Text>
              {group.entries.map((entry) => (
                <Link
                  key={entry.term}
                  className={styles.termButton}
                  to={{
                    search: `?${createSearchParams({ term: entry.term })}`,
                  }}
                  replace
                  aria-current={
                    entry.term === selectedTerm ? "true" : undefined
                  }
                >
                  {entry.term}
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <article className={styles.detail}>
          <span className={styles.termLabel}>Begriff</span>
          <Text variant="subheader">{selectedEntry.term}</Text>
          {selectedEntry.longDescription ? (
            <BlockContent content={selectedEntry.longDescription} />
          ) : (
            <Text variant="body">{selectedEntry.description}</Text>
          )}
        </article>
      </div>
    </PageShell>
  );
};

export default GlossaryOverview;
